import { BaselineDemographicsForm as BaselineDemographicsFormDto, LookupGenderType, LookupKnownUnknownType, LookupNoYesType, LookupSexType, LookupYesNoUnknownType, Permission } from "@/api/dtos";
import { positiveWithPrecision } from "@/utils/NumberFormats";
import { Grid2, Grid2Props, Tooltip, TooltipProps } from "@mui/material";
import { ConditionContext, ConditionMode, useFormActions, useFormState, ValidateOn } from "@ngt/forms-core";
import { Autocomplete, Checkbox, CollaboratingGroupContext, CrfCondition, CrfForm, DatePicker, FormBreadcrumbs, GroupedField, GroupedFields, ICrfConditionParameters, InstitutionContext, MasterGroupContext, Numeric, PageLayout, PatientContext, RadioGroup, RouteLoading, Select, Text, ToggleButtonGroup, useCountryById } from "@ngt/opms";
import { crfReadOnly } from "@/utils/CrfFormUtils";
import { usePermissionsByIds } from "@ngt/opms-bctapi";
import { FC, useCallback, useContext, useEffect } from "react";
import LabelWithTooltip from "../labels/LabelWithTooltip";

interface IBaselineDemographicsFormProps {

}

interface IFormContentProps {

}

interface ICalculatedFieldsComponentProps {

}

interface IGridWithTooltipProps extends Grid2Props {
    TooltipProps: TooltipProps
}

const GridWithTooltip: FC<IGridWithTooltipProps> = ({
    TooltipProps,
    ...rest
}) => {

    // const { readOnly } = useContext(FormOptionsContext);

    const { conditionMet } = useContext(ConditionContext) ?? {};

    return (
        conditionMet ?
        <Tooltip
            arrow
            placement={"bottom"}
            {...TooltipProps}
        >
            <Grid2 {...rest} />
        </Tooltip> :
        <Grid2 {...rest} />
    )
}


export const CalculateFieldsComponent: FC<ICalculatedFieldsComponentProps> = () => {
    const { setFieldValue } = useFormActions();

    const { value } = useFormState({value: true});

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            if(!value.height || !value.weight || value.height <= 0) {
                setFieldValue("bmi", undefined);
                return;
            }

            const bmi = Math.floor(value.weight/(value.height*value.height));
            setFieldValue("bmi", bmi);
        }, 500);

        return () => window.clearTimeout(timeout);
    }, [value.weight, value.height])

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            const primaryDiagnosisDateStr = value.primaryDiagnosisDate;
            const metastaticDiagnosisDateStr = value.metastaticDiagnosisDate;

            if(!primaryDiagnosisDateStr || !metastaticDiagnosisDateStr) {
                setFieldValue("diseaseFreeInterval", undefined);
            }

            const primaryDiagnosisDate = new Date(primaryDiagnosisDateStr as string);
            const metastaticDiagnosisDate = new Date(metastaticDiagnosisDateStr as string);

            // const yearDiff = metastaticDiagnosisDate.year - primaryDiagnosisDate.year - 1 + (
            //     metastaticDiagnosisDate.month > primaryDiagnosisDate.month || 
            //     (metastaticDiagnosisDate.month == primaryDiagnosisDate.month && metastaticDiagnosisDate.day >= primaryDiagnosisDate.day) ? 1 : 0
            // );

            const weekDiff = Math.floor((metastaticDiagnosisDate.getTime() - primaryDiagnosisDate.getTime())/(1000*60*60*24*7));

            setFieldValue("diseaseFreeInterval", weekDiff);
        }, 500);

        return () => window.clearTimeout(timeout);
    }, [value.primaryDiagnosisDate, value.metastaticDiagnosisDate])
    
    return (
        <>
        </>
    )
}

export const BaselineDemographicsFormContent: FC<IFormContentProps> = () => {
    const { data: institution } = useContext(InstitutionContext);
    const { data: country } = useCountryById(institution?.countryId);

    const isAustralianParticipant = useCallback(() => country?.name == "Australia", [country]);
    // const notAustralianParticipant = useCallback(() => !isAustralianParticipant(), [isAustralianParticipant]);

    const her2CopyNumberKnown = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.her2CopyNumberStatus == LookupKnownUnknownType.Known,
    []);

    const ethnicityNotUnknown = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.ethnicityUnknown !== true,
    []);

    const notBirthCountryOther = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.birthCountryOther != true,
    []);
    const birthCountryOther = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.birthCountryOther == true,
    []);

    const genderOther = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.gender == LookupGenderType.Other,
    []);

    const notPrimaryNonEnglishLanguageOther = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.primaryNonEnglishLanguageOther != true,
    []);

    const notPrimaryLanguageAsChildOther = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.primaryLanguageAsChildOther != true,
    []);

    const ecogAnswered = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.ecog != null,
    []);

    // const heightAnswered = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
    //     !!params.formState?.value?.height,
    // []);

    const weightAnswered = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        !!params.formState?.value?.weight,
    []);

    const biologicalSexNotMale = useCallback((params: ICrfConditionParameters<BaselineDemographicsFormDto>) => 
        params.formState?.value?.sex != LookupSexType.Male,
    []);
    
    return (
        <>
            <CalculateFieldsComponent />
            <GroupedField
                propertyPath="sex"
                component={Autocomplete}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <GroupedField
                propertyPath="gender"
                component={Autocomplete}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <CrfCondition
                mode={ConditionMode.Show}
                condition={genderOther}
            >
                <GroupedField
                    propertyPath="genderOtherSpecify"
                    component={Text}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            <CrfCondition
                mode={ConditionMode.Show}
                condition={isAustralianParticipant}
            >
                <GroupedField
                    propertyPath="postcode"
                    component={Text}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            <GroupedFields>
                <CrfCondition
                    condition={ethnicityNotUnknown}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="ethnicity"
                        component={Autocomplete}
                        FieldGridProps={{
                            size: {
                                xl: 5,
                                lg: 5,
                                md: 5,
                                sm: 5,
                                xs: 12
                            },
                            component: (props) => (
                                <GridWithTooltip 
                                    {...props} 
                                    TooltipProps={{
                                        title: "Type to search ethnicities"
                                    }} 
                                />
                            )
                        }}
                        ContainerGridProps={{
                            justifyContent: "space-between"
                        }}
                        LabelGridProps={{
                            size: {
                                xl: 5,
                                lg: 5,
                                md: 5,
                                sm: 5,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <GroupedField
                    propertyPath="ethnicityUnknown"
                    component={Checkbox}
                />
            </GroupedFields>
            <CrfCondition
                mode={ConditionMode.Show}
                condition={isAustralianParticipant}
            >
                <GroupedFields>
                    <CrfCondition
                        condition={notBirthCountryOther}
                        mode={ConditionMode.Enable}
                    >
                        <GroupedField
                            propertyPath="birthCountry"
                            component={Autocomplete}
                            FieldGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                },
                                component: (props) => (
                                    <GridWithTooltip 
                                        {...props} 
                                        TooltipProps={{
                                            title: "Type to search countries"
                                        }} 
                                    />
                                )
                            }}
                            ContainerGridProps={{
                                justifyContent: "space-between"
                            }}
                            LabelGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                    <GroupedField
                        propertyPath="birthCountryOther"
                        component={Checkbox}
                    />
                    <CrfCondition
                        condition={birthCountryOther}
                        mode={ConditionMode.Show}
                    >
                        <GroupedField
                            propertyPath="birthCountryOtherSpecify"
                            component={Text}
                            FieldGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                </GroupedFields>
            </CrfCondition>
            <CrfCondition
                mode={ConditionMode.Show}
                condition={isAustralianParticipant}
            >
                <GroupedFields>
                    <CrfCondition
                        condition={notPrimaryLanguageAsChildOther}
                        mode={ConditionMode.Enable}
                    >
                        <GroupedField
                            propertyPath="primaryLanguageAsChild"
                            component={Autocomplete}
                            FieldGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                },
                                component: (props) => (
                                    <GridWithTooltip 
                                        {...props} 
                                        TooltipProps={{
                                            title: "Type to search countries"
                                        }} 
                                    />
                                )
                            }}
                            ContainerGridProps={{
                                justifyContent: "space-between"
                            }}
                            LabelGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                    <GroupedField
                        propertyPath={"primaryLanguageAsChildOther"}
                        component={Checkbox}
                    />
                    <CrfCondition
                        condition={(form) => form.formState?.value.primaryLanguageAsChildOther == true}
                        mode={ConditionMode.Show}
                    >
                        <GroupedField
                            propertyPath="primaryLanguageAsChildOtherSpecify"
                            component={Text}
                            FieldGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                </GroupedFields>
            </CrfCondition>
            <CrfCondition
                mode={ConditionMode.Show}
                condition={isAustralianParticipant}
            >
                <GroupedFields>
                    <CrfCondition
                        condition={notPrimaryNonEnglishLanguageOther}
                        mode={ConditionMode.Enable}
                    >
                        <GroupedField
                            propertyPath="primaryNonEnglishLanguage"
                            component={Autocomplete}
                            FieldGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                },
                                component: (props) => (
                                    <GridWithTooltip 
                                        {...props} 
                                        TooltipProps={{
                                            title: "Type to search languages"
                                        }} 
                                    />
                                )
                            }}
                            ContainerGridProps={{
                                justifyContent: "space-between"
                            }}
                            LabelGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                    <GroupedField
                        propertyPath={"primaryNonEnglishLanguageOther"}
                        component={Checkbox}
                    />
                    <CrfCondition
                        condition={(form) => form.formState?.value.primaryNonEnglishLanguageOther == true}
                        mode={ConditionMode.Show}
                    >
                        <GroupedField
                            propertyPath="primaryNonEnglishLanguageOtherSpecify"
                            component={Text}
                            FieldGridProps={{
                                size: {
                                    xl: 5,
                                    lg: 5,
                                    md: 5,
                                    sm: 5,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                </GroupedFields>
            </CrfCondition>
            <CrfCondition
                condition={biologicalSexNotMale}
                mode={ConditionMode.Enable}
            >
                <GroupedField
                    propertyPath="menopauseStatus"
                    component={Autocomplete}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            {/* <CrfCondition
                condition={(params) => params.formState?.value.ethnicity == LookupEthnicityType.Other}
                mode={ConditionMode.Show}
            >
                <GroupedField
                    propertyPath="ethnicityOtherSpecify"
                    component={Text}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition> */}
            <GroupedField
                propertyPath="deNovoMetastaticDisease"
                component={ToggleButtonGroup}
            />
            <CrfCondition
                condition={(params) => params.formState?.value.deNovoMetastaticDisease == LookupNoYesType.No}
                mode={ConditionMode.Show}
            >
                <GroupedField
                    propertyPath="primaryDiagnosisDate"
                    component={DatePicker}
                    format="dd-MMM-yyyy"
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            <GroupedField
                propertyPath="metastaticDiagnosisDate"
                component={DatePicker}
                format="dd-MMM-yyyy"
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <CrfCondition
                condition={(params) => params.formState?.value.deNovoMetastaticDisease == LookupNoYesType.No}
                mode={ConditionMode.Show}
            >
                <GroupedField
                    propertyPath="diseaseFreeInterval"
                    component={Numeric}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                    disabled
                />
            </CrfCondition>
            <GroupedField
                propertyPath="erStatus"
                component={Autocomplete}
                LabelProps={{label: <LabelWithTooltip content={"Metastatic biopsy where available, if not then from primary biopsy"}/> as any}}                
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <GroupedField
                propertyPath="prStatus"
                component={Autocomplete}
                LabelProps={{label: <LabelWithTooltip content={"Metastatic biopsy where available, if not then from primary biopsy"}/> as any}} 
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <GroupedField
                propertyPath="her2Status"
                component={Autocomplete}
                LabelProps={{label: <LabelWithTooltip content={"Metastatic biopsy where available, if not then from primary biopsy"}/> as any}} 
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <GroupedField
                propertyPath="her2Ihc"
                component={Autocomplete}
                LabelProps={{label: <LabelWithTooltip content={"Metastatic biopsy where available, if not then from primary biopsy"}/> as any}} 
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <GroupedField
                propertyPath="her2CopyNumberStatus"
                component={Autocomplete}
                LabelProps={{label: <LabelWithTooltip content={"Metastatic biopsy where available, if not then from primary biopsy"}/> as any}} 
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <CrfCondition
                condition={her2CopyNumberKnown}
                mode={ConditionMode.Show}
            >
                <GroupedField
                    propertyPath="her2CopyNumberValue"
                    component={Numeric}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            <GroupedField
                propertyPath="linesPreviousTherapy"
                component={Select}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <GroupedField
                propertyPath="ecog"
                component={Select}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <CrfCondition
                condition={ecogAnswered}
                mode={ConditionMode.Enable}
            >
                <GroupedField
                    propertyPath="ecogAssessmentDate"
                    component={DatePicker}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            <GroupedField
                propertyPath="height"
                component={Numeric}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
                numberFormat={positiveWithPrecision(2)}
            />
            <GroupedField
                propertyPath="weight"
                component={Numeric}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
                numberFormat={positiveWithPrecision(1)}
            />
            <CrfCondition
                condition={weightAnswered}
                mode={ConditionMode.Enable}
            >
                <GroupedField
                    propertyPath="weightAssessmentDate"
                    component={DatePicker}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            <GroupedField
                propertyPath="bmi"
                component={Numeric}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
                disabled
            />
            <GroupedField
                propertyPath="priorPregnancy"
                component={Select}
                FieldGridProps={{
                    size: {
                        xl: 5,
                        lg: 5,
                        md: 5,
                        sm: 5,
                        xs: 12
                    }
                }}
            />
            <CrfCondition
                condition={(params) => params.formState?.value.priorPregnancy == LookupYesNoUnknownType.Yes}
                mode={ConditionMode.Show}
            >
                <GroupedField
                    propertyPath="gravidity"
                    component={Text}
                    LabelProps={{label: <LabelWithTooltip content={"Write a gravidity #"}/> as any}} 
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            <CrfCondition
                condition={(params) => params.formState?.value.priorPregnancy == LookupYesNoUnknownType.Yes}
                mode={ConditionMode.Show}
            >
                <GroupedField
                    propertyPath="parity"
                    component={Text}
                    LabelProps={{label: <LabelWithTooltip content={"Write a parity #"}/> as any}} 
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
            <CrfCondition
                condition={(params) => params.formState?.value.priorPregnancy == LookupYesNoUnknownType.Yes}
                mode={ConditionMode.Show}
            >
                <GroupedField
                    propertyPath="pregnancyRelatedNausea"
                    component={RadioGroup}
                    FieldGridProps={{
                        size: {
                            xl: 5,
                            lg: 5,
                            md: 5,
                            sm: 5,
                            xs: 12
                        }
                    }}
                />
            </CrfCondition>
        </>
    )
}

const BaselineDemographicsForm: FC<IBaselineDemographicsFormProps> = () => {

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm 
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                enableQueries
            >
                <BaselineDemographicsFormContent />
            </CrfForm>
        </PageLayout>
    )
}

export default BaselineDemographicsForm;